import { ComponentProps, forwardRef } from 'react'

import { pxToRem } from '@/shared/styles/themes'
import { IconButton as MuiIconButton, styled } from '@mui/material'

const sizeMap = {
  small: {
    size: 32,
    borderRadius: 4,
    fontSize: 16,
  },
  medium: {
    size: 36,
    borderRadius: 8,
    fontSize: 16,
  },
  large: {
    size: 48,
    borderRadius: 12,
    fontSize: 20,
  },
} as const

export type IconButtonProps = Omit<ComponentProps<typeof MuiIconButton>, 'color' | 'disableRipple'> & {
  primary?: boolean
  round?: boolean
  tooltipTitle?: string
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ primary, tooltipTitle, round = true, ...props }, ref) => (
    <StyledIconButton
      $primary={primary}
      $round={round}
      disableRipple
      ref={ref}
      {...props}
    />
  ),
)

const StyledIconButton = styled(MuiIconButton, {
  shouldForwardProp: (prop) => prop !== '$primary' && prop !== '$round',
}) <{ $primary?: boolean; $round?: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  color: ${props => props.theme.palette.text.primary};
  background-color: transparent;
  min-width: 0;
  line-height: 0;
  font-weight: 400;
  padding: 0;

  width: ${props => pxToRem(sizeMap[props.size || 'medium'].size)};
  height: ${props => pxToRem(sizeMap[props.size || 'medium'].size)};

  .MuiSvgIcon-root {
    font-size: ${props => pxToRem(sizeMap[props.size || 'medium'].fontSize)};
  }

  &:before,
  &:after {
    content: '';
    position: absolute;
    z-index: 1;
    top: 0;
    bottom: 0;
    right: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border-radius: ${props => props.$round ? '50%' : pxToRem(sizeMap[props.size || 'medium'].borderRadius)};
    background-color: rgba(0, 0, 0, 0.05);
    opacity: 0;
    transform: scaleX(0.7) scaleY(0.7) perspective(1px);
  }

  &:before {
    transition: transform 0.25s cubic-bezier(0.35, 0.12, 0.14, 1.4), opacity 0.25s ease;
  }

  &:after {
    transition: transform 0.2s cubic-bezier(0.35, 0.12, 0.14, 1.4), opacity 0.1s ease;
  }

  &:hover {
    background-color: transparent;

    &:after,
    &:before {
      opacity: 1;
      background-color: rgba(0, 0, 0, 0.05);
      transform: scaleX(1.015) scaleY(1.035) perspective(1px);
    }
  }

  &:active {
    &:after,
    &:before {
      transform: scaleX(0.95) scaleY(0.95) perspective(1px);
    }
  }

  ${props => props.$primary && `
    color: ${props.theme.palette.primary.main};
  `}
`